import type PriceStackLuxPlusProminentPrice from 'LuxKit/PricePoints/LuxPlus/PriceStackLuxPlusProminentPrice'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import type { ComponentProps } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import PriceStackPriceWithLoyalty from 'LuxKit/PricePoints/PriceStackPriceWithLoyalty'
import PriceStackPriceWithLoyaltyLeadWithRegularPrice from 'LuxKit/PricePoints/PriceStackPriceWithLoyaltyLeadWithRegularPrice'
import PriceRowLuxPlusPriceBox from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceBox'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceStackLuxPlusCompactPrice from 'LuxKit/PricePoints/LuxPlus/PriceStackLuxPlusCompactPrice'
import PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice from 'LuxKit/PricePoints/PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice'
import { isGoogleHotelAdsBot } from 'selectors/accountSelectors'
import { isGhaPriceDisplay } from 'selectors/routerSelectors'
import useLuxPlusDisplayedMode from 'luxPlus/hooks/useLuxPlusDisplayedMode'

type InheritedPriceRowPriceProps = Pick<ComponentProps<typeof PriceRowPrice>, 'signDisplay' | 'size'>
type InheritedPriceStackPriceWithLoyaltyProps = Pick<ComponentProps<typeof PriceStackPriceWithLoyalty>, 'horizontalAlign' | 'desktopHorizontalAlign'>

interface CommonProps extends InheritedPriceRowPriceProps, InheritedPriceStackPriceWithLoyaltyProps {
  testid?: string
  /**
   * @default vertical
   */
  orientation?: ComponentProps<typeof PriceStackLuxPlusProminentPrice>['orientation']
  offerConfig?: App.OfferLuxPlusConfig
  isTour?: boolean
  mode?: App.LuxPlusPriceStackMode
}

interface PropsWithSeparatePriceFields extends CommonProps {
  price: number
  saleUnit: string
  memberPrice?: number
}

interface PropsWithPricingField extends CommonProps {
  pricing: App.Pricing
  memberPricing?: App.Pricing
}

type Props = PropsWithSeparatePriceFields | PropsWithPricingField

function LuxPlusPriceStack(props: Props) {
  const {
    size,
    testid,
    signDisplay,
    horizontalAlign = 'start',
    desktopHorizontalAlign,
    orientation = 'vertical',
    offerConfig,
    isTour = false,
    mode,
  } = props

  let memberPrice: number = 0
  let price: number
  let saleUnit: string

  if ('pricing' in props) {
    price = props.pricing.price
    saleUnit = props.pricing.saleUnit
    memberPrice = props.memberPricing?.price || 0
  } else {
    price = props.price
    saleUnit = props.saleUnit
    memberPrice = props.memberPrice || 0
  }

  const isGoogleHotelAds = useAppSelector(isGoogleHotelAdsBot)
  const isGhaPricing = useAppSelector(isGhaPriceDisplay)

  const displayedMode = useLuxPlusDisplayedMode({ price, memberPrice, offerConfig, isTour, mode })

  switch (displayedMode) {
    case 'member-only':
      return <PriceRowLuxPlusPriceBox
        data-testid={testid}
        memberPrice={memberPrice || price}
        saleUnit={saleUnit}
        size={size}
        horizontalAlign={horizontalAlign}
      />
    case 'regular-only':
      return <PriceRowPrice
        data-testid={testid}
        price={price}
        saleUnit={saleUnit}
        size={size}
        signDisplay={signDisplay}
      />
    case 'member-prominent': {
      return <PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        nonMemberPrice={price}
        size={size}
        signDisplay={signDisplay}
        horizontalAlign={horizontalAlign}
        orientation={orientation}
        desktopHorizontalAlign={desktopHorizontalAlign}
      />
    }
    case 'member-compact':
      return <PriceStackLuxPlusCompactPrice
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        nonMemberPrice={price}
        size={size}
        horizontalAlign={horizontalAlign}
      />
    case 'regular-prominent':
      if (isGhaPricing) {
        return <PriceStackPriceWithLoyaltyLeadWithRegularPrice
          data-testid={testid}
          memberPrice={memberPrice}
          saleUnit={saleUnit}
          nonMemberPrice={price}
          size={size}
          signDisplay={signDisplay}
          horizontalAlign={horizontalAlign}
          desktopHorizontalAlign={desktopHorizontalAlign}
        />
      }
      if (isGoogleHotelAds) {
        return <PriceStackPriceWithLoyalty
          data-testid={testid}
          memberPrice={memberPrice}
          saleUnit={saleUnit}
          nonMemberPrice={price}
          size={size}
          signDisplay={signDisplay}
          horizontalAlign={horizontalAlign}
          desktopHorizontalAlign={desktopHorizontalAlign}
        />
      }
      return <PriceStackPriceWithLoyaltyLeadWithLuxPlusPrice
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        nonMemberPrice={price}
        size={size}
        signDisplay={signDisplay}
        horizontalAlign={horizontalAlign}
        orientation={orientation}
        desktopHorizontalAlign={desktopHorizontalAlign}
      />

    case 'member-checkout-total':
      return <PriceRowLuxPlusPriceBox
        data-testid={testid}
        memberPrice={memberPrice || price}
        saleUnit={saleUnit}
        size={size}
        horizontalAlign={horizontalAlign}
      />
    case 'regular-with-luxPlus':
      return <PriceRowLuxPlusPrice
        size="L"
        memberPrice={memberPrice}
        logoPlacement="trailing"
        saleUnit=""
      />
  }
}

export default LuxPlusPriceStack
